/** Public claim page for a dual-OTP committee invite.
 *
 *  Flow:
 *    1. Peek the invite via the owner-only dev-peek is not available
 *       here (public flow) — codes must be entered manually.
 *    2. User inputs both OTPs + name + password.
 *    3. On success the API returns access_token + user + org info + a
 *       quorum block; we auto-sign-in via the AuthContext and redirect.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function CommitteeVerify() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [invitePreview, setInvitePreview] = useState(null);
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [quorumFlash, setQuorumFlash] = useState(null);

  // The dual-OTP peek is owner-only, so this landing shows a generic
  // shell that requires the candidate to enter both codes manually.
  // We surface only the token in the URL as evidence of the invite.
  useEffect(() => {
    // Optional single-channel peek endpoint at /committee-invites/{token}
    // works for the older flow — try it as a best-effort preview.
    api.get(`/committee-invites/${token}`)
       .then(({ data }) => setInvitePreview(data))
       .catch(() => setInvitePreview({ token }));
  }, [token]);

  const canSubmit =
    emailOtp.length === 6 && phoneOtp.length === 6
    && fullName.trim().length >= 2 && password.length >= 6 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/associations/committee/verify", {
        token,
        email_otp: emailOtp.trim(),
        phone_otp: phoneOtp.trim(),
        full_name: fullName.trim(),
        password,
      });
      const flip = Boolean(data.quorum?.association_status_changed);
      if (flip) {
        setQuorumFlash(data.quorum);
      }
      toast.success(`Seated as ${data.user?.organization_role?.replace(/_/g," ")}`);
      // Store token + refresh AuthContext
      if (data.access_token) {
        setToken(data.access_token);
      }
      await refresh?.();
      // Read the flip flag directly (not from `quorumFlash` state — that
      // React state update isn't visible in this closure yet).
      setTimeout(() => nav("/dashboard"), flip ? 2500 : 400);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const org = invitePreview?.organization;

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white flex items-center justify-center px-4 py-12"
         data-testid="committee-verify-page">
      <div className="max-w-lg w-full cs-card rounded-2xl p-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5B82E] flex items-center gap-2">
          <ShieldCheck size={11} /> Committee Verification
        </div>
        <h1 className="font-display text-3xl uppercase tracking-tight mt-3">
          {org?.brand_name ? `Join ${org.brand_name}` : "Verify Your Seat"}
        </h1>
        <p className="mt-2 text-sm text-white/60">
          {invitePreview?.role_label
            ? <>You&rsquo;ve been invited as <span className="text-white font-semibold">{invitePreview.role_label}</span>. Enter the two 6-digit codes sent to your email and phone to confirm.</>
            : <>Enter both 6-digit codes to activate your committee seat.</>}
        </p>

        {quorumFlash && (
          <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-start gap-2"
               data-testid="quorum-flash">
            <Sparkles size={14} className="text-emerald-300 mt-0.5" />
            <div className="text-[11px] text-emerald-200">
              <strong>Quorum reached!</strong> {quorumFlash.active_members} active members — the association is now fully activated.
            </div>
          </div>
        )}

        <div className="mt-6 space-y-4">
          <OtpField label="Email OTP" value={emailOtp} onChange={setEmailOtp}
                    testid="email-otp-input" />
          <OtpField label="Phone OTP" value={phoneOtp} onChange={setPhoneOtp}
                    testid="phone-otp-input" />
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Full Name</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15"
                   data-testid="verify-fullname-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Set Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15"
                   data-testid="verify-password-input" />
          </div>
          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2"
                 data-testid="verify-error">
              <AlertCircle size={14} className="text-red-300 mt-0.5" />
              <div className="text-[11px] text-red-200">{error}</div>
            </div>
          )}
          <Button onClick={submit} disabled={!canSubmit}
                  className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                  data-testid="verify-submit-btn">
            {busy ? "Verifying…" : <><KeyRound size={13} className="mr-2" /> Verify & Sign In</>}
          </Button>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 text-center pt-2">
            Codes expire in 48 h · 10 attempts per invite
          </div>
        </div>
      </div>
    </div>
  );
}

function OtpField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <Input value={value}
             onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
             maxLength={6} placeholder="000000"
             className="mt-2 h-12 bg-black/40 border-white/15 font-mono text-2xl tracking-[0.4em] text-center"
             data-testid={testid} autoComplete="one-time-code" />
    </div>
  );
}
